import { useState, useEffect, useContext } from "react";
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableHead,
    TableRow,
    TablePagination,
    Toolbar,
    IconButton,
    Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getSummary, exportSummaryList, getEndPoint } from "api/layer";
import { AppContext, AppAction } from "helpers/context";
import styles from "./burialPointStatistic.module.scss";
import SearchInput from "./search-input/search-input";
import StartTimeDropdown from "./start-time-dropdown/start-time-dropdown";
import EndTimeDropdown from "./end-time-dropdown/end-time-dropdown";


function BurialPointStatistic() {
    const [context, dispatch] = useContext(AppContext);
    const [t] = useTranslation();
    const [showFilters, setShowFilters] = useState(false);
    const [searchData, setSearchData] = useState<{
        keyWord: string;
        startTime: any;
        endTime: any;
    }>({
        keyWord: "",
        startTime: null,
        endTime: null,
    });
    const [reverseSelect, setReverseSelect] = useState(false);
    const [page, setPage] = useState(0);
    const [tableData, setTableData] = useState([]);
    const [totalStatistic, setTotalStatistic] = useState<any>({});
    const [endPointOptions, setEndPointOptions] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [sortByField, setSortByField] = useState("total_calls");
    const [sortOrder, setSortOrder] = useState<Record<string, "asc" | "desc">>({
        start_time: "asc",
        end_time: "asc",
        client_name: "asc",
        bill_name: "asc",
        key_code: "asc",
        total_calls: "asc"
    });

    const toggleFilters = () => {
        setShowFilters((prev) => !prev);
    };

    const hanldeDownload = () => {
        const filteredData = Object.fromEntries(
            Object.entries(searchData).filter(([key, value]) => value !== null && value !== '')
        );
        if (filteredData.startTime) {
            filteredData.startTime = filteredData.startTime.format("YYYY-MM-DD");
        }
        if (filteredData.endTime) {
            filteredData.endTime = filteredData.endTime.format("YYYY-MM-DD");
        }
        exportSummaryList({ ...filteredData, language: context.language }).then((res: any) => {
            const url = window.URL.createObjectURL(new Blob([res], { type: "application/force-download;charset=utf-8" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${t("userSystem.burialPointStatistic")}.xls`);
            document.body.appendChild(link);
            link.click();
            link.onload = () => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            };
        });
    };

    const handleSearch = (params: any, is: boolean) => {
        setSearchData((prevData) => ({ ...prevData, [params.name]: params.value }));
        if ((params.name === "startTime" || params.name === "endTime") && params.value && params.value.format("YYYY-MM-DD") === "Invalid Date") {
            return;
        }
        setReverseSelect(is);
        setPage(0);
    };

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const initData = async () => {
        setIsLoading(true);
        let pageParam = {
            pageNo: page + 1,
            pageSize: rowsPerPage,
            reverseSelect: reverseSelect,
            sortOrderAsc: sortOrder[sortByField] === "asc",
            sortByField: sortByField,
            ...searchData
        };

        let params: any = { ...searchData };

        if (searchData.startTime) {
            params.startTime = searchData.startTime.format("YYYY-MM-DD");
        }

        if (searchData.endTime) {
            params.endTime = searchData.endTime.format("YYYY-MM-DD");
        }

        getSummary(params, pageParam).then((res: any) => {
            if (res.code !== 200 || !res.data?.rows) {
                return;
            }
            setTableData(res.data.rows.map((item: any) => {
                item.endpoint_stats = JSON.parse(item.endpoint_stats || "{}");
                return item;
            }));
            setTotalCount(res.data.totalCount * 1 || 0);
            res.data.totalStatistic.endpoint_stats = JSON.parse(res.data.totalStatistic.endpoint_stats || "{}");
            setTotalStatistic(res.data.totalStatistic)
            setIsLoading(false);
        });
    };

    const handleGetEndPoint = () => {
        getEndPoint(undefined, {
            pageNo: 1,
            pageSize: 1000,
            sortByField: "name",
            sortOrderAsc: true,
        }).then((res: any) => {
            if (res.code !== 200 || !res.data?.rows) {
                return;
            }
            setEndPointOptions(res.data.rows || []);
            setSortOrder((prev) => {
                let sort: any = { ...prev };
                res.data.rows.forEach((item: any) => {
                    sort[item.name] = "asc";
                });
                return sort;
            });
        });
    };

    const statusText = (status: string) => {
        switch (status) {
            case "00":
                return "success"
            case "01":
                return "failed"
            default:
                return "failed"
        }
    }

    useEffect(() => {
        if (endPointOptions.length === 0) return;
        initData();
    }, [page, rowsPerPage, searchData, reverseSelect, sortByField, sortOrder]);

    useEffect(() => {
        handleGetEndPoint();
    }, [])


    return (
        <div className={styles.container}>
            <Toolbar style={{ justifyContent: "space-between", borderBottom: "1px solid #ccc", marginBottom: "5px", padding: "0" }}>
                <div>
                    <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        style={{ padding: "4px 10px", marginRight: "10px" }}
                        onClick={hanldeDownload}
                    >
                        {t("buttons.download")}
                    </Button>
                </div>

                <IconButton
                    color="success"
                    onClick={toggleFilters}
                    style={{
                        color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
                        background: showFilters ? "#c1e5ca" : "",
                    }}
                >
                    <span className="material-icons">filter_list</span>
                </IconButton>
            </Toolbar>

            {showFilters && (
                <Box style={{ display: "flex", alignItems: "center", padding: "8px", backgroundColor: "#f5f5f5", justifyContent: "space-between" }}>
                    <Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
                        <SearchInput onSearch={handleSearch} />

                        <StartTimeDropdown
                            defaultTime={searchData.startTime}
                            onSave={(value) => handleSearch({ name: "startTime", value }, reverseSelect)}
                        />
                        <EndTimeDropdown
                            defaultTime={searchData.endTime}
                            onSave={(value) => handleSearch({ name: "endTime", value }, reverseSelect)}
                        />
                    </Box>
                    <IconButton
                        onClick={() => setShowFilters(false)}
                        aria-label="clear"
                        color="default"
                    >
                        <span className="material-icons">clear</span>
                    </IconButton>
                </Box>
            )}

            <div style={{ flex: 1, overflow: "auto" }}>
                <Table
                    stickyHeader
                    aria-label="sticky  table"
                    size="small"
                    style={{
                        tableLayout: "fixed",
                        width: "100%",
                        minWidth: "100%"
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell style={{
                                whiteSpace: "nowrap",
                                padding: "6px",
                                width: "120px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "start_time"}
                                    onClick={() => {
                                        setSortByField("start_time")
                                        let sort: any = {
                                            ...sortOrder,
                                            start_time: sortOrder.start_time === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.start_time}
                                >

                                    {t("userSystem.startDate")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                whiteSpace: "nowrap",
                                padding: "6px",
                                width: "120px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "end_time"}
                                    onClick={() => {
                                        setSortByField("end_time")
                                        let sort: any = {
                                            ...sortOrder,
                                            end_time: sortOrder.end_time === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.end_time}
                                >

                                    {t("userSystem.endDate")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                whiteSpace: "nowrap",
                                padding: "6px",
                                width: "120px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "client_name"}
                                    onClick={() => {
                                        setSortByField("client_name")
                                        let sort: any = {
                                            ...sortOrder,
                                            client_name: sortOrder.client_name === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.client_name}
                                >
                                    {t("userSystem.clientName")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                whiteSpace: "nowrap",
                                padding: "6px",
                                width: "120px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "bill_name"}
                                    onClick={() => {
                                        setSortByField("bill_name")
                                        let sort: any = {
                                            ...sortOrder,
                                            bill_name: sortOrder.bill_name === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.bill_name}
                                >

                                    {t("userSystem.billName")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                whiteSpace: "nowrap",
                                padding: "6px",
                                width: "120px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "key_code"}
                                    onClick={() => {
                                        setSortByField("key_code")
                                        let sort: any = {
                                            ...sortOrder,
                                            key_code: sortOrder.key_code === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.key_code}
                                >

                                    {t("userSystem.keyCode")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                whiteSpace: "nowrap",
                                padding: "6px",
                                width: "180px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "total_calls"}
                                    onClick={() => {
                                        setSortByField("total_calls")
                                        let sort: any = {
                                            ...sortOrder,
                                            total_calls: sortOrder.total_calls === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.total_calls}
                                >
                                    {t("userSystem.totalCalls")}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{
                                whiteSpace: "nowrap",
                                padding: "6px",
                                width: "180px"
                            }}>
                                <TableSortLabel
                                    active={sortByField === "status"}
                                    onClick={() => {
                                        setSortByField("status")
                                        let sort: any = {
                                            ...sortOrder,
                                            status: sortOrder.status === "asc" ? "desc" : "asc"
                                        }
                                        setSortOrder(sort)
                                    }}
                                    direction={sortOrder.status}
                                >
                                    {t("userSystem.status")}
                                </TableSortLabel>
                            </TableCell>
                            {
                                endPointOptions.map((item: any) => {
                                    return (
                                        <TableCell
                                            style={{
                                                wordBreak: "break-all",
                                                padding: "6px",
                                                width: "150px"
                                            }}
                                            key={item.id}
                                        >
                                            <TableSortLabel
                                                active={sortByField === item.name}
                                                onClick={() => {
                                                    setSortByField(item.name)
                                                    let sort: any = {
                                                        ...sortOrder,
                                                        [item.name]: sortOrder[item.name] === "asc" ? "desc" : "asc"
                                                    }
                                                    setSortOrder(sort)
                                                }}
                                                direction={sortOrder[item.name]}
                                            >
                                                {item.name}</TableSortLabel>
                                        </TableCell>
                                    )
                                })
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={(6 + endPointOptions.length)}>
                                    <div className={styles.loading}>
                                        <div className="loading loading-spinner loading-lg"></div>
                                        <div>{t("layers.loading")}</div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (<>
                            <TableRow>
                                <TableCell style={{ padding: "6px" }} colSpan={2}>{t("userSystem.grandTotal")}</TableCell>
                                <TableCell style={{ padding: "6px" }}>{totalStatistic.client_name}</TableCell>
                                <TableCell style={{ padding: "6px" }}>{totalStatistic.bill_name}</TableCell>
                                <TableCell style={{ padding: "6px" }}>{totalStatistic.key_code}</TableCell>
                                <TableCell style={{ padding: "6px" }}>{totalStatistic.total_calls}</TableCell>
                                <TableCell style={{ padding: "6px" }}>{totalStatistic.status}</TableCell>
                                {endPointOptions.map((ep: any) => (
                                    <TableCell
                                        style={{ padding: "6px" }}
                                        key={ep.id}
                                    >
                                        {totalStatistic.endpoint_stats[ep.name]}
                                    </TableCell>
                                ))}
                            </TableRow>
                            {tableData.map((item: any, index: number) => (
                                <TableRow
                                    key={index}
                                    className={styles.row}
                                >
                                    <TableCell style={{ padding: "6px" }}>{item.start_time}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>{item.end_time}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>{item.client_name}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>{item.bill_name}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>{item.key_code}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>{item.total_calls}</TableCell>
                                    <TableCell style={{ padding: "6px" }}>{t("userSystem." + statusText(item.status))}</TableCell>
                                    {endPointOptions.map((ep: any) => (
                                        <TableCell
                                            style={{ padding: "6px" }}
                                            key={ep.id}
                                        >
                                            {item.endpoint_stats[ep.name]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div style={{ marginLeft: "auto" }}>
                <TablePagination
                    component="div"
                    count={totalCount}
                    rowsPerPageOptions={[3, 10, 15, 25]}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("userSystem.rowsPerPage")}
                    slotProps={{
                        select: {
                            native: true,
                        },
                    }}
                />
            </div>
        </div >
    );
}

export default BurialPointStatistic;
